/**
 * ShowroomListHeader — hero banner + country tabs (CF-032).
 * Image: CSS backgroundImage. Video: HeroBannerBackgroundMedia.
 */

import {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import {
  Box,
  Heading,
  Skeleton,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {HeroBannerBackgroundMedia} from '@amblique/king-living/app/components/hero-banner-background-media';
import {HERO_BANNER_APPEARANCE} from '@amblique/king-living/app/contentful/models/hero-banner/hero-banner-appearance';
import {getContainerStyles} from '@amblique/king-living/app/theme/components/project/showroom-list-header';
import {
  HERO_BANNER_MOBILE_NATURAL_VIDEO_OPTIONS,
  resolveHeroBackgroundLayers,
} from '@amblique/king-living/app/utils/hero-banner-video';

const CountryTab = ({country, isActive, onSelect, styles}) => {
  const {code, label, total} = country;
  const isClickable = !isActive && typeof total === 'number' && total > 0;
  const count =
    total != null ? (
      <Text as="sup" {...styles.countBadge}>
        {total}
      </Text>
    ) : null;

  if (isActive) {
    return (
      <Text
        aria-current="page"
        as="span"
        className="showroom-country-tab is-active"
        {...styles.countryLink}
        {...styles.countryLinkActive}
      >
        {label}
        {count}
      </Text>
    );
  }

  if (!isClickable) {
    return (
      <Text
        as="span"
        className="showroom-country-tab is-disabled"
        {...styles.countryLink}
        {...styles.countryLinkDisabled}
      >
        {label}
        {count}
      </Text>
    );
  }

  return (
    <Box
      as="button"
      className="showroom-country-tab"
      type="button"
      {...styles.countryLink}
      onClick={() => onSelect?.(code)}
    >
      {label}
      {count}
    </Box>
  );
};

CountryTab.propTypes = {
  country: PropTypes.shape({
    code: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    total: PropTypes.number,
  }).isRequired,
  isActive: PropTypes.bool,
  onSelect: PropTypes.func,
  styles: PropTypes.object.isRequired,
};

export const ShowroomListHeader = ({
  heroData,
  countries,
  selectedCountryCode,
  onCountrySelect,
  isLoading = false,
  ...props
}) => {
  const {formatMessage} = useIntl();
  const styles = useMultiStyleConfig('ShowroomListHeader', {});

  const fallbackImage = getAssetUrl('static/img/showroom-header-bg.jpg');
  const backgroundMedia = heroData?.backgroundMedia;
  const backgroundVideoMobile = heroData?.backgroundVideoMobile;

  const {backgroundImage, desktopVideo, mobileVideo} = useMemo(
    () => resolveHeroBackgroundLayers({backgroundMedia, backgroundVideoMobile}),
    [backgroundMedia, backgroundVideoMobile]
  );

  const hasVideo = Boolean(desktopVideo || mobileVideo);
  const {mobileAsset, tabletAsset, desktopAsset} = backgroundImage || {};
  const hasHeroBackgroundImage = Boolean(
    mobileAsset?.url || tabletAsset?.url || desktopAsset?.url
  );

  const isCf032Appearance =
    heroData?.appearance === HERO_BANNER_APPEARANCE.SHOWROOMS_HEADER_CF_032;
  const cmsHeadline = heroData?.headingTitle;
  const hasCountries = Boolean(countries?.length);
  const hasContentSection = Boolean(cmsHeadline || hasCountries);
  const isMobileNaturalVideo = hasVideo && !hasContentSection;

  const backgroundImageByBreakpoint = useMemo(() => {
    if (hasVideo) {
      return null;
    }

    if (!hasHeroBackgroundImage) {
      return {base: fallbackImage, md: fallbackImage, lg: fallbackImage};
    }

    return {
      base: mobileAsset?.url || tabletAsset?.url || desktopAsset?.url || fallbackImage,
      md: tabletAsset?.url || desktopAsset?.url || mobileAsset?.url || fallbackImage,
      lg: desktopAsset?.url || tabletAsset?.url || mobileAsset?.url || fallbackImage,
    };
  }, [
    hasVideo,
    hasHeroBackgroundImage,
    mobileAsset,
    tabletAsset,
    desktopAsset,
    fallbackImage,
  ]);

  // Fallback title only on /showrooms (has country tabs).
  const title =
    cmsHeadline ||
    (hasCountries
      ? formatMessage({
          defaultMessage: 'Visit a King showroom',
          id: 'showroom_list_header.fallback_title',
        })
      : null);

  const containerStyles = useMemo(() => {
    const base = getContainerStyles();

    if (!backgroundImageByBreakpoint) {
      return base;
    }

    const toBackgroundUrl = (url) => (url ? `url('${url}')` : undefined);

    return {
      ...base,
      sx: {
        ...base.sx,
        backgroundImage: {
          base: toBackgroundUrl(backgroundImageByBreakpoint.base),
          md: toBackgroundUrl(backgroundImageByBreakpoint.md),
          lg: toBackgroundUrl(backgroundImageByBreakpoint.lg),
        },
      },
    };
  }, [backgroundImageByBreakpoint]);

  const wrapperClassName = [
    'showroom-list-header',
    'hero-banner',
    isMobileNaturalVideo && 'showroom-list-header--video-no-content',
  ]
    .filter(Boolean)
    .join(' ');

  const appearanceAttrs = isCf032Appearance
    ? {'data-appearance': HERO_BANNER_APPEARANCE.SHOWROOMS_HEADER_CF_032}
    : {};

  if (isLoading) {
    return (
      <Box
        className="showroom-list-header hero-banner"
        {...appearanceAttrs}
        {...containerStyles}
        {...props}
      >
        <Skeleton height="300px" width="100%" />
      </Box>
    );
  }

  return (
    <Box className={wrapperClassName} {...appearanceAttrs} {...containerStyles} {...props}>
      {hasVideo ? (
        <Box className="showroom-list-header__video-media" {...styles.videoMedia}>
          <HeroBannerBackgroundMedia
            backgroundMedia={backgroundMedia}
            backgroundVideoMobile={backgroundVideoMobile}
            height={isMobileNaturalVideo ? undefined : '100%'}
            mobileVideoOptions={
              isMobileNaturalVideo ? HERO_BANNER_MOBILE_NATURAL_VIDEO_OPTIONS : undefined
            }
            width="100%"
          />
        </Box>
      ) : null}

      {!isMobileNaturalVideo ? (
        <>
          <Box className="showroom-list-header__overlay" {...styles.overlay} />
          <Box className="showroom-list-header-content-wrapper" {...styles.contentWrapper}>
            {title ? (
              <Heading as="h1" {...styles.title}>
                {title}
              </Heading>
            ) : null}

            {hasCountries ? (
              <Box className="showroom-list-header__countries" {...styles.countryLinksContainer}>
                {countries.map((country) => (
                  <CountryTab
                    key={country.code}
                    country={country}
                    isActive={country.code === selectedCountryCode}
                    styles={styles}
                    onSelect={onCountrySelect}
                  />
                ))}
              </Box>
            ) : null}
          </Box>
        </>
      ) : null}
    </Box>
  );
};

ShowroomListHeader.displayName = 'ShowroomListHeader';
ShowroomListHeader.propTypes = {
  heroData: PropTypes.shape({
    appearance: PropTypes.string,
    headingTitle: PropTypes.string,
    backgroundMedia: PropTypes.object,
    backgroundVideoMobile: PropTypes.object,
  }),
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      total: PropTypes.number,
    })
  ),
  selectedCountryCode: PropTypes.string,
  onCountrySelect: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default ShowroomListHeader;
